import { useEffect, useRef, useState } from "react";
import api from "../../../../services/api";

/**
 * Small in-memory cache + background revalidation.
 * Key is derived from {showLegacy, dateOn, startDate, endDate}.
 */
export default function useOrdersData({
  showLegacy,
  dateOn,
  startDate,
  endDate,
}) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const cacheRef = useRef(new Map()); // key -> { data, fetchedAt }

  const cacheKey = () =>
    [
      showLegacy ? "legacy" : "new",
      dateOn || "pickup",
      startDate || "",
      endDate || "",
    ].join("|");

  const fetchOrders = async ({ force = false, useCachedFirst = true } = {}) => {
    const key = cacheKey();
    const params = new URLSearchParams();
    if (startDate) params.append("from", startDate);
    if (endDate) params.append("to", endDate);
    if (dateOn) params.append("dateOn", dateOn);

    const endpoint = showLegacy
      ? `/legacy-orders?${params.toString()}`
      : `/orders?${params.toString()}`;

    const cached = cacheRef.current.get(key);

    if (!force && useCachedFirst && cached) {
      setOrders(cached.data);
      setLoading(false);
    }

    try {
      const { data } = await api.get(endpoint);
      setOrders(data);
      cacheRef.current.set(key, { data, fetchedAt: Date.now() });
      setLastRefreshedAt(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchOrders({ useCachedFirst: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLegacy, dateOn, startDate, endDate]);

  const refresh = () => {
    setLoading(true);
    fetchOrders({ force: true, useCachedFirst: false });
  };

  return { orders, loading, refresh, lastRefreshedAt };
}
